import { useQuery } from "@tanstack/react-query";

import { reportsApi } from "@/lib/api/reports";
import { departmentsApi } from "@/lib/api/departments";
import { branchesApi } from "@/lib/api/branches";
import { employeesApi } from "@/lib/api/employees";
import { notificationsApi } from "@/lib/api/notifications";
import { leavesApi } from "@/lib/api/leaves";
import { expenseApi } from "@/lib/api/expense";
import { hrDashboardApi } from "@/lib/api/hrDashboard";
import { separationsApi } from "@/lib/api/separations";
import { employeeLifecycleApi } from "@/lib/api/employeeLifecycle";
import { recruitmentApi } from "@/lib/api/recruitment";
import { performanceApi } from "@/lib/api/performance";
import { documentsApi } from "@/lib/api/documents";
import { complianceApi } from "@/lib/api/compliance";
import { assetsApi } from "@/lib/api/assets";
import { qk } from "@/lib/query-client";
import { can, useAuthStore } from "@/stores/auth-store";
import { useFeatureEnabled } from "@/lib/features";

// One hook per data source a widget reads. Each uses the same query key as
// the original dashboard (and as the module dashboards), so a figure shown by
// two widgets - or by a widget and the rest of the app - is fetched once, and
// a widget that is hidden never runs its query at all because it is simply
// not mounted. Every hook also repeats the permission + module check the
// original dashboard applies: the server is the real gate, this just avoids
// firing requests that would 403.

/** The month summary behind the KPI cards (headcount, attendance mix, payroll). */
export function useDashboardReport() {
  return useQuery({ queryKey: qk.reports.dashboard, queryFn: reportsApi.dashboard });
}

function useEmployeeDirectoryAccess(): boolean {
  const enabled = useFeatureEnabled("employee_management");
  return enabled && can("employee.read");
}

export function useHeadcount() {
  const allowed = useEmployeeDirectoryAccess();
  return useQuery({ queryKey: qk.employees.headcount, queryFn: employeesApi.headcount, enabled: allowed });
}

/** The 100 most recently joined on-roll employees: the roster page the
 * original dashboard derives New Joinees and the hiring trend from. */
export function useRecentJoiners() {
  const allowed = useEmployeeDirectoryAccess();
  const params = { sort: "joining_date", order: "desc", on_roll: true, per_page: 100 } as const;
  return useQuery({ queryKey: qk.employees.list(params), queryFn: () => employeesApi.list(params), enabled: allowed });
}

export function useDepartments() {
  const allowed = useEmployeeDirectoryAccess();
  return useQuery({ queryKey: qk.departments.list({ per_page: 100 }), queryFn: () => departmentsApi.list({ per_page: 100 }), enabled: allowed });
}

export function useDepartmentSummary() {
  const allowed = useEmployeeDirectoryAccess();
  return useQuery({ queryKey: qk.departments.summary, queryFn: () => departmentsApi.summary(), enabled: allowed });
}

export function useBranches() {
  const branchesEnabled = useFeatureEnabled("branch_management");
  return useQuery({
    queryKey: qk.branches.list({ per_page: 100 }),
    queryFn: () => branchesApi.list({ per_page: 100 }),
    enabled: branchesEnabled && can("branch.read"),
  });
}

export function useRecentLeaves() {
  const leave = useFeatureEnabled("leave_management");
  return useQuery({ queryKey: qk.leaves.list({ per_page: 6 }), queryFn: () => leavesApi.list({ per_page: 6 }), enabled: leave && can("leave.read") });
}

export function useExpenseSummary() {
  const expense = useFeatureEnabled("expense_management");
  return useQuery({ queryKey: qk.expense.summary, queryFn: expenseApi.summary, enabled: expense && can("expense.view_reports") });
}

export function useGeoSnapshot() {
  const geo = useFeatureEnabled("geo_fencing");
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: qk.reports.geoFenceSummary,
    queryFn: () => reportsApi.geoFenceSummary(),
    enabled: geo && !user?.is_super_admin && can("report.read"),
  });
}

export function useRecentActivity() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: qk.notifications.list({ per_page: 6 }),
    queryFn: () => notificationsApi.list({ per_page: 6 }),
    enabled: !user?.is_super_admin,
  });
}

/** Today's roster, on-leave count, pending documents and open tickets - the
 * HR dashboard's aggregate, already scoped on the server to the caller's
 * branch / department assignments. */
export function useHrToday() {
  const employeeMgmt = useFeatureEnabled("employee_management");
  return useQuery({ queryKey: qk.hrDashboard, queryFn: hrDashboardApi.get, enabled: employeeMgmt && can("hr.dashboard.view") });
}

export function useExitStats() {
  const employeeMgmt = useFeatureEnabled("employee_management");
  return useQuery({ queryKey: qk.separations.dashboard, queryFn: separationsApi.dashboardStats, enabled: employeeMgmt && can("employee.separation.manage") });
}

export function useProbationStats() {
  const employeeMgmt = useFeatureEnabled("employee_management");
  const onboarding = useFeatureEnabled("employee_onboarding");
  return useQuery({
    queryKey: qk.probation.stats,
    queryFn: employeeLifecycleApi.probationStats,
    enabled: employeeMgmt && onboarding && can("employee.lifecycle.read"),
  });
}

export function useRecruitmentStats() {
  const recruitment = useFeatureEnabled("recruitment");
  return useQuery({ queryKey: qk.recruitment.dashboard, queryFn: () => recruitmentApi.dashboard(), enabled: recruitment && can("recruitment.dashboard.view") });
}

export function usePerformanceStats() {
  const performance = useFeatureEnabled("performance_management");
  return useQuery({ queryKey: qk.performance.dashboard, queryFn: performanceApi.dashboard, enabled: performance && can("performance.dashboard.view") });
}

export function useDocumentStats() {
  const documents = useFeatureEnabled("document_management");
  return useQuery({ queryKey: qk.document.dashboard, queryFn: documentsApi.dashboard, enabled: documents && can("document.view_reports") });
}

export function useComplianceStats() {
  const compliance = useFeatureEnabled("statutory_compliance");
  return useQuery({ queryKey: qk.compliance.summary, queryFn: complianceApi.summary, enabled: compliance && can("compliance.read") });
}

export function useAssetStats() {
  const assets = useFeatureEnabled("asset_management");
  return useQuery({ queryKey: qk.assets.dashboard, queryFn: assetsApi.dashboard, enabled: assets && can("assets.dashboard.view") });
}
